import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';
import { MyProjectsService } from '@projects/workspace/src/app/my-projects/my-projects.service';
import { CryptUtilService } from '../../crypt-util.service';
import { PsgService } from '@projects/psg/src/app/psg/psg.service';
import { environment } from '../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProjectAccessGuard implements CanActivate {
  constructor(
    private router: Router,
    private myProjectsService: MyProjectsService,
    private cyrptUtilService: CryptUtilService,
    private psgService: PsgService
  ) {}

  public isEUInstance(): boolean {
    if (environment.isLocal)
      return false;
    const euDomain = environment.GlOBAL_INSTANCE_DOMAINS?.EU?.toLowerCase();
    // Return false if euDomain is empty/undefined to prevent false positives
    if (!euDomain) {
      return false;
    }
    return window.location.host.toLowerCase().includes(euDomain);
  }

  async canActivate(
    route: ActivatedRouteSnapshot
  ): Promise<boolean> {
      const currentUrl = window.location.pathname;
      const isProjectSummaryPage = currentUrl.includes('/projectsummary/');
      const isMasterProjectFromStorage = this.cyrptUtilService.getItem('isMasterProject', 'LOCAL');
      const isMasterProjectSummary = isProjectSummaryPage && isMasterProjectFromStorage === true;
      const isEUEnviroment = this.isEUInstance();
      
      let projectType = '';

        // For EU instance, read isMasterProject from URL query params and store in localStorage
        if (isEUEnviroment) {
            const urlParams = new URLSearchParams(window.location.search);
            
            // First check raw isMasterProject param (from list redirect)
            const rawMasterProject = urlParams.get('isMasterProject') === 'true';
            if (rawMasterProject) {
                this.cyrptUtilService.setItem('isMasterProject', rawMasterProject, 'LOCAL');
            } else {
                // Fallback: check encrypted initialSetup param (from project creation)
                const encryptedSetup = urlParams.get('initialSetup');
                if (encryptedSetup) {
                    const decoded = decodeURIComponent(encryptedSetup);
                    const data = this.cyrptUtilService.decryptObject(decoded);
                    if (data?.isPMOProject){
                      projectType = 'PMO';
                      // Persist to localStorage for EU instance (mirrors US instance logic)
                      const commonFeatureFlag = this.cyrptUtilService.getItem('commonFeatureFlag', 'LOCAL') || {};
                      commonFeatureFlag.projectType = 'PMO';
                      commonFeatureFlag.multiRelease = 'false';
                      this.cyrptUtilService.setItem('commonFeatureFlag', commonFeatureFlag, 'LOCAL');
                      // PMO projects should always have isMasterProject = false
                      this.cyrptUtilService.setItem('isMasterProject', false, 'LOCAL');
                    }
                    console.log('Decoded initialSetup:', this.cyrptUtilService.decryptObject(decoded));
                    const isMasterProject = this.cyrptUtilService.decryptObject(decoded)?.isMasterProject;
                    // Only set isMasterProject for non-PMO projects (PMO already handled above)
                    if (isMasterProject && !data?.isPMOProject) {
                        this.cyrptUtilService.setItem('isMasterProject', isMasterProject, 'LOCAL');
                    }
                }
            }
        }



    let projectDetailInfo: any;
    let isAscendPMO = false;
    let isAIGProject = false;
    let isMasterProject = false;
    // Check multiRelease feature flag from admin db configuration
    let multiReleaseEnabled = (this.cyrptUtilService.getItem('commonFeatureFlag', 'LOCAL'))?.multiRelease === 'true';

    // while creating the PMO project
    if (!projectType)
    projectType = this.cyrptUtilService.getItem('commonFeatureFlag', 'LOCAL')?.projectType || '';

    // If flag is false, verify with backend API
    if (!multiReleaseEnabled && projectType !== 'PMO') {
      try {
        const res: any = await this.myProjectsService.getMultiReleaseFeatureFlag().toPromise();
        // Find multiRelease from data array
        const multiReleaseConfig = res?.data?.find(item => item.configName === 'multiRelease');
        multiReleaseEnabled = multiReleaseConfig?.configValue === 'true';
      } catch (error) {
        console.error('Failed to fetch feature flag:', error);
        // On error, keep multiReleaseEnabled as false
      }
    }

    // Only use isMasterProject flag if multiRelease is enabled, otherwise always use v1 APIs
    isMasterProject = multiReleaseEnabled
      ? (this.cyrptUtilService.getItem('isMasterProject', 'LOCAL') || false)
      : false;


    // Check all possible parameter names for project ID across different routes
    const projectIdParam = route.params['projectId'] ||
      route.params['projectid'] ||
      route.params['selectedProjectId'] ||
      route.params['id'] ||
      route.queryParams['projectId'] ||
      route.queryParams['projectid'] ||
      route.queryParams['selectedProjectId'];

    // If no projectId parameter is found, allow navigation (this route doesn't need project validation)
    if (!projectIdParam) {
      return true;
    }

    const projectId = parseInt(projectIdParam, 10);

    // At the time of project creation allow URLs to pass with projectId = 0
    if (projectId === 0) {
      return true;
    }
    // If projectId exists but is invalid, redirect to 404
    if (isNaN(projectId)) {
      this.router.navigate(['/404']);
      return false;
    }
    
    try {
      if (!isMasterProject || (isMasterProjectSummary)) {
        const data = await this.psgService.getInitProjectInfo(projectId).toPromise();
        if (data) {
          projectDetailInfo = data;
          isAscendPMO = projectDetailInfo[0]?.PMOProject === 1 ? true : false;
          isAIGProject = projectDetailInfo[0]?.AIGProject === 1 ? true : false;
        }
      }
    } catch (error) {
      console.error('Failed to fetch project data:', error);
      // Set defaults if fetch fails
      isAscendPMO = false;
      isAIGProject = false;
    }



    // Update localStorage to ensure consistency across all components
    // while accesing the different projects
    if (!multiReleaseEnabled) {
      this.cyrptUtilService.setItem("isMasterProject", false, "LOCAL");
      isMasterProject = false;
    } 
    // for master projects 
    else if (multiReleaseEnabled && isMasterProject && !isAscendPMO && !isAIGProject) {
      this.cyrptUtilService.setItem("isMasterProject", true, "LOCAL");
      isMasterProject = true;
    }
    // for PMO and AIG projects
    else if(multiReleaseEnabled && (isAscendPMO || isAIGProject)) {
      const commonFeatureFlag = this.cyrptUtilService.getItem('commonFeatureFlag', 'LOCAL') || {};
      commonFeatureFlag.multiRelease = 'false';
      this.cyrptUtilService.setItem('commonFeatureFlag', commonFeatureFlag, 'LOCAL');
      this.cyrptUtilService.setItem("isMasterProject", false, "LOCAL");
      isMasterProject = false;
    }
    // for release projects which are not master projects
    else if(multiReleaseEnabled && !isMasterProject && !isAscendPMO && !isAIGProject){
      this.cyrptUtilService.setItem("isMasterProject", false, "LOCAL");
      isMasterProject = false;
    }

    // Block access to expired release projects via direct URL manipulation
    // Case 1: isMasterProject is false - projectDetailInfo is already fetched above
    if (multiReleaseEnabled && !isMasterProject && !isAscendPMO && !isAIGProject && projectDetailInfo) {
      const projectData = projectDetailInfo[0];
      if (projectData?.isExpired || projectData?.projectStatus === 'EXPIRED') {
        this.router.navigate(['/404']);
        return false;
      }
    }
    // Case 2: isMasterProject is true (user may have altered URL from master project context)
    // Fetch project info via v1 API separately without modifying isMasterProject state
    if (multiReleaseEnabled && isMasterProject && !isAscendPMO && !isAIGProject) {
      try {
        const releaseData = await this.psgService.getInitProjectInfo(projectId).toPromise();
        if (releaseData && releaseData[0]) {
          const releaseProject = releaseData[0];
          // Only block if project is expired AND it's not a master project (PMO/AIG already excluded above)
          if (releaseProject?.isExpired || releaseProject?.projectStatus === 'EXPIRED') {
            this.router.navigate(['/404']);
            return false;
          }
        }
      } catch (error) {
        // If v1 API fails, it's likely a genuine master project - continue normally
      }
    }

    try {
      const projectStatus: any = await this.myProjectsService.getProjectStatus(projectId, isMasterProject).toPromise();
      // Check if project exists and is not soft deleted
      if (!projectStatus || projectStatus.data === true) {
        this.router.navigate(['/404']);
        return false;
      }
      return true; // project exists and not deleted
    } catch (error) {
      try {
        const projectStatus: any = await this.myProjectsService.getProjectStatus(projectId, true).toPromise();
        // Check if project exists and is not soft deleted
        if (!projectStatus || projectStatus.data === true) {
          this.router.navigate(['/404']);
          return false;
        }
        
        // Set isMasterProject to true in localStorage
        this.cyrptUtilService.setItem('isMasterProject', true, 'LOCAL');
        console.log('Setting isMasterProject to true after getProjectStatus call');
        
        return true;
      } catch (statusError) {
        console.error('Failed to fetch project status:', statusError);
        // If project doesn't exist or API fails, redirect to 404
        this.router.navigate(['/404']);
        return false;
      }
    }
  }
}
